import { activeProgram, blocksForDate, phaseForDate } from "../domain/planning";
import type { ExercisePlan, ExerciseSession, ISODate, ProgramVersion, SetEntry, WorkoutSession } from "../domain/types";
import { putWorkout, workoutForDate } from "./db";

export async function ensureWorkout(date: ISODate, programs: ProgramVersion[]): Promise<WorkoutSession> {
  const existing = await workoutForDate(date);
  if (existing) return reconcileWorkout(existing, programs, date);
  const now = new Date().toISOString();
  const program = activeProgram(programs, date);
  const phase = phaseForDate(program, date);
  const blocks = blocksForDate(program, phase, date);
  const exerciseSessions: ExerciseSession[] = blocks.flatMap((block) =>
    block.exercises.map((exercise) => createExerciseSession(exercise, now))
  );
  const session: WorkoutSession = {
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    revision: 1,
    date,
    programVersionId: program.id,
    phaseId: phase.id,
    dayKind: "training",
    startedAt: now,
    exerciseSessions
  };
  await putWorkout(session);
  return session;
}

async function reconcileWorkout(session: WorkoutSession, programs: ProgramVersion[], date: ISODate): Promise<WorkoutSession> {
  const program = activeProgram(programs, date);
  const phase = phaseForDate(program, date);
  const blocks = blocksForDate(program, phase, date);
  const orderedPlans = blocks.flatMap((block) => block.exercises);
  const plans = new Map(orderedPlans.map((exercise) => [exercise.id, exercise]));
  let changed = false;
  const nextExercises: ExerciseSession[] = [];
  for (const plan of orderedPlans) {
    const exercise = session.exerciseSessions.find((item) => item.exercisePlanId === plan.id) ?? createExerciseSession(plan, new Date().toISOString());
    if (!session.exerciseSessions.some((item) => item.exercisePlanId === plan.id)) changed = true;
    changed = reconcileExercise(exercise, plan) || changed;
    nextExercises.push(exercise);
  }
  for (const exercise of session.exerciseSessions) {
    const plan = plans.get(exercise.exercisePlanId);
    const hasUserData = exercise.sets.some((set) => set.done);
    if (!plan && hasUserData) nextExercises.push(exercise);
    if (!plan && !hasUserData) changed = true;
  }
  session.exerciseSessions = nextExercises;
  if (changed) await putWorkout(session);
  return session;
}

function createExerciseSession(exercise: ExercisePlan, now: string): ExerciseSession {
  return {
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    revision: 1,
    exercisePlanId: exercise.id,
    name: exercise.name,
    target: exercise.target,
    tracking: exercise.tracking,
    notes: exercise.notes,
    sets: Array.from({ length: exercise.sets }, (_, index): SetEntry => ({
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
      revision: 1,
      setNumber: index + 1,
      plannedReps: exercise.defaultReps,
      plannedSeconds: exercise.defaultSeconds,
      reps: exercise.defaultReps,
      seconds: exercise.defaultSeconds,
      done: false
    }))
  };
}

function reconcileExercise(exercise: ExerciseSession, plan: ExercisePlan): boolean {
  let changed = false;
  if (exercise.name !== plan.name || exercise.target !== plan.target || exercise.tracking !== plan.tracking || exercise.notes !== plan.notes) {
    exercise.name = plan.name;
    exercise.target = plan.target;
    exercise.tracking = plan.tracking;
    exercise.notes = plan.notes;
    changed = true;
  }
  if (exercise.sets.length !== plan.sets) {
    const now = new Date().toISOString();
    exercise.sets = Array.from({ length: plan.sets }, (_, index) => exercise.sets[index] ?? {
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
      revision: 1,
      setNumber: index + 1,
      done: false
    });
    changed = true;
  }
  for (const set of exercise.sets) {
    const oldAutoValue = set.plannedReps;
    if (plan.defaultReps !== undefined && (set.reps === undefined || set.reps === oldAutoValue || set.reps === plan.sets)) {
      set.reps = plan.defaultReps;
      changed = true;
    }
    if (plan.defaultReps === undefined && set.reps !== undefined && !set.done) {
      set.reps = undefined;
      changed = true;
    }
    if (plan.defaultSeconds !== undefined && (set.seconds === undefined || set.seconds === set.plannedSeconds)) {
      set.seconds = plan.defaultSeconds;
      changed = true;
    }
    if (plan.defaultSeconds === undefined && set.seconds !== undefined && !set.done) {
      set.seconds = undefined;
      changed = true;
    }
    if (set.plannedReps !== plan.defaultReps || set.plannedSeconds !== plan.defaultSeconds) {
      set.plannedReps = plan.defaultReps;
      set.plannedSeconds = plan.defaultSeconds;
      changed = true;
    }
  }
  return changed;
}
